'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import NewsletterOverlay from './newsletter';
import LogoInfo from '../logoInfo';

type RichTextChild = {
  type: 'text' | 'link';
  text?: string;
  url?: string;
  children?: { text: string }[];
};

type RichTextBlock = {
  children: RichTextChild[];
};

type MenuItem = {
  name: string;
  link: string;
};

type FooterData = {
  top_left_side_text: string;
  terms_and_conditions_link: string;
  terms_and_conditions_text: string;
  site_by_text: string;
  join_the_newsletter_text: string;
  menu: MenuItem[];
  about_tbc: {
    address: string;
    description: RichTextBlock[]; // ✅ corrected type
    title: string;
  };
};

type HeaderProps = {
  globalData: {
    footer: FooterData;
  };
};

export default function Footer({ globalData }: HeaderProps) {
  const [isNewsletterOpen, setNewsletterOpen] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // fallback
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const footerData = globalData?.footer;
  return (
    <>
      <motion.footer
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6 }}
        className="bg-[#2a3329] text-[#e5e1d5] py-6 md:py-5"
      >
        <div className="container-main mx-auto px-4 md:px-5">
          <motion.div
            className="flex justify-between items-start mb-[110px] md:mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <p className="text-[16px] font-light leading-none">{footerData.top_left_side_text}</p>
            <button
              onClick={scrollToTop}
              className="text-[16px] font-light leading-none hover:text-[#B6C380] backtotop"
            >
              Back to top
            </button>
          </motion.div>

          <motion.div
            className="text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <ul className="text-xl md:text-[26px] font-light flex leading-none flex-col md:flex-row footer-menu justify-center gap-3 md:gap-2">
              {footerData?.menu.map((item, index) => (
                <li key={index}>
                  <Link href={item.link} className="hover:text-[#B6C380]">
                    {item.name}
                  </Link>
                  <span>,</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="flex justify-center items-end mt-[110px] md:mt-[17.4rem] text-[13px] font-light text-[#b5c380] gap-2 md:gap-7 middle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center mb-0 logo-foot">
              <LogoInfo fillColor="#b5c380" />
            </div>
          </motion.div>

          <motion.div
            className="justify-between leading-none grid grid-cols-1 md:grid-cols-3 mt-0 md:mt-0 bottom"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="space-x-2 lg:space-x-4 hidden md:flex">
              <Link
                href={footerData.terms_and_conditions_link}
                className="text-[12px] lg:text-[14px] font-light text-[#b5c380] hover:text-[#E5E1D5] text-start"
              >
                {footerData.terms_and_conditions_text}
              </Link>
              <p className="text-[12px] lg:text-[14px] font-light text-[#b5c380]">
                {footerData.site_by_text}
              </p>
            </div>
            <div>
              <p className="text-[12px] font-light text-[#b5c380] text-center hidden md:block">
                {footerData.about_tbc.title}
              </p>
              {/* <p className="text-[12px] font-light text-[#b5c380] text-center md:hidden">
                {footerData.top_left_side_text}
              </p> */}
            </div>
            <div className="mt-6 md:mt-0 text-end hidden md:block">
              <p className="text-[12px] lg:text-[14px] font-light">
                <button
                  type="button"
                  className="text-[#b5c380] hover:text-[#E5E1D5]"
                  onClick={(e) => {
                    e.preventDefault();
                    setNewsletterOpen(true);
                  }}
                >
                  {footerData.join_the_newsletter_text}
                </button>
              </p>
            </div>
          </motion.div>
        </div>
      </motion.footer>

      <NewsletterOverlay isOpen={isNewsletterOpen} onClose={() => setNewsletterOpen(false)} />
    </>
  );
}
